<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const labelWithColon = key => `${t(key)}:`;

const isLoading = ref(false);
const hasLoaded = ref(false);
const overview = ref({
  waba: {},
  current_phone: {},
  phone_numbers: [],
  subscribed_apps: [],
});

const loadOverview = async () => {
  isLoading.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppPhoneManagement(
      props.inbox.id
    );
    overview.value = {
      waba: response.data?.waba || {},
      current_phone: response.data?.current_phone || {},
      phone_numbers: response.data?.phone_numbers || [],
      subscribed_apps: response.data?.subscribed_apps || [],
    };
    hasLoaded.value = true;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.active,
  isActive => {
    if (isActive && !hasLoaded.value) loadOverview();
  },
  { immediate: true }
);
</script>

<template>
  <div class="grid gap-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_NOTE') }}
      </p>
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-ph-arrows-clockwise"
        :label="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_REFRESH')
        "
        :is-loading="isLoading"
        @click="loadOverview"
      />
    </div>

    <div
      v-if="isLoading"
      class="rounded-xl border border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_LOADING') }}
    </div>

    <template v-else>
      <div class="grid gap-4 lg:grid-cols-2">
        <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
          <p class="mb-3 text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_WABA') }}
          </p>
          <div class="grid gap-2 text-sm text-n-slate-11">
            <p class="mb-0">
              <strong>{{
                labelWithColon(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_ID'
                )
              }}</strong>
              {{ overview.waba.id || '-' }}
            </p>
            <p class="mb-0">
              <strong>{{
                labelWithColon(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_NAME'
                )
              }}</strong>
              {{ overview.waba.name || '-' }}
            </p>
            <p class="mb-0">
              <strong>{{
                labelWithColon(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_CURRENCY'
                )
              }}</strong>
              {{ overview.waba.currency || '-' }}
            </p>
            <p class="mb-0">
              <strong>{{
                labelWithColon(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_TIMEZONE'
                )
              }}</strong>
              {{ overview.waba.timezone_id || '-' }}
            </p>
            <p class="mb-0 break-all">
              <strong>{{
                labelWithColon(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_NAMESPACE'
                )
              }}</strong>
              {{ overview.waba.message_template_namespace || '-' }}
            </p>
          </div>
        </div>

        <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
          <p class="mb-3 text-sm font-semibold text-n-slate-12">
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_CURRENT_PHONE'
              )
            }}
          </p>
          <div class="grid gap-2 text-sm text-n-slate-11">
            <p class="mb-0">
              <strong>{{
                labelWithColon(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_ID'
                )
              }}</strong>
              {{ overview.current_phone.id || '-' }}
            </p>
            <p class="mb-0">
              <strong>{{
                labelWithColon(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_DISPLAY_NUMBER'
                )
              }}</strong>
              {{ overview.current_phone.display_phone_number || '-' }}
            </p>
            <p class="mb-0">
              <strong>{{
                labelWithColon(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_VERIFIED_NAME'
                )
              }}</strong>
              {{ overview.current_phone.verified_name || '-' }}
            </p>
            <p class="mb-0">
              <strong>{{
                labelWithColon(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_QUALITY'
                )
              }}</strong>
              {{
                overview.current_phone.quality_rating ||
                overview.current_phone.quality_score ||
                '-'
              }}
            </p>
            <p class="mb-0">
              <strong>{{
                labelWithColon(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_STATUS'
                )
              }}</strong>
              {{
                overview.current_phone.status ||
                overview.current_phone.code_verification_status ||
                '-'
              }}
            </p>
            <p class="mb-0">
              <strong>{{
                labelWithColon(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_DISPLAY_STATUS'
                )
              }}</strong>
              {{ overview.current_phone.name_status || '-' }}
            </p>
          </div>
        </div>
      </div>

      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-3 text-sm font-semibold text-n-slate-12">
          {{
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_PHONE_NUMBERS'
            )
          }}
        </p>

        <div
          v-if="!overview.phone_numbers.length"
          class="rounded-xl border border-dashed border-n-container bg-n-background px-4 py-8 text-center text-sm text-n-slate-11"
        >
          {{
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_PHONE_NUMBERS_EMPTY'
            )
          }}
        </div>

        <div v-else class="grid gap-3">
          <div
            v-for="phone in overview.phone_numbers"
            :key="phone.id"
            class="rounded-xl border border-n-container bg-n-background p-4"
          >
            <p class="mb-1 text-sm font-semibold text-n-slate-12">
              {{ phone.display_phone_number || phone.id }}
            </p>
            <p class="mb-1 text-xs text-n-slate-11">
              {{
                [
                  phone.verified_name || '-',
                  phone.quality_rating || phone.quality_score || '-',
                ].join(' · ')
              }}
            </p>
            <p class="mb-0 text-xs text-n-slate-11">
              {{
                [
                  phone.status || phone.code_verification_status || '-',
                  phone.name_status || '-',
                ].join(' · ')
              }}
            </p>
          </div>
        </div>
      </div>

      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-3 text-sm font-semibold text-n-slate-12">
          {{
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_SUBSCRIBED_APPS'
            )
          }}
        </p>

        <div
          v-if="!overview.subscribed_apps.length"
          class="rounded-xl border border-dashed border-n-container bg-n-background px-4 py-8 text-center text-sm text-n-slate-11"
        >
          {{
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_SUBSCRIBED_APPS_EMPTY'
            )
          }}
        </div>

        <div v-else class="grid gap-3">
          <div
            v-for="app in overview.subscribed_apps"
            :key="
              app.whatsapp_business_api_data?.link ||
              app.id ||
              JSON.stringify(app)
            "
            class="rounded-xl border border-n-container bg-n-background p-4 text-sm text-n-slate-11"
          >
            <p class="mb-1 font-semibold text-n-slate-12">
              {{
                app.name ||
                app.id ||
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_APP_FALLBACK'
                )
              }}
            </p>
            <p class="mb-0 break-all">
              {{ app.whatsapp_business_api_data?.link || app.link || '-' }}
            </p>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
